import { useState } from "react";
import {
  Palette,
  Image as ImageIcon,
  Layout,
  Maximize2,
  Minimize2,
  Sliders,
  Upload,
  Trash2,
  Columns as ColumnsIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Check,
  RefreshCw,
  Type,
  ShieldAlert,
  ShieldCheck,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Zap,
} from "lucide-react";
import { useEditorStore } from "../store";
import type { SiteNode } from "@zirro/schema";
import {
  parseColor,
  blendOverlay,
  evaluateContrast,
} from "../contrast-utils";

interface SectionCustomizerProps {
  node: SiteNode;
}

export type SectionPanelTab = "background" | "image" | "typography" | "layout" | "spacing";

const GRADIENT_PRESETS = [
  {
    id: "midnight",
    label: "Midnight",
    gradient: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    from: "#0f172a",
    to: "#1e293b",
  },
  {
    id: "sunset",
    label: "Sunset",
    gradient: "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
    from: "#f97316",
    to: "#ec4899",
  },
  {
    id: "ocean",
    label: "Ocean",
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)",
    from: "#0ea5e9",
    to: "#3b82f6",
  },
  {
    id: "emerald",
    label: "Emerald",
    gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
    from: "#059669",
    to: "#10b981",
  },
  {
    id: "aurora",
    label: "Aurora",
    gradient: "linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)",
    from: "#6366f1",
    to: "#ec4899",
  },
  {
    id: "warm",
    label: "Warm Gold",
    gradient: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
    from: "#fef3c7",
    to: "#fde68a",
  },
  {
    id: "slate",
    label: "Dark Slate",
    gradient: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
    from: "#1e293b",
    to: "#0f172a",
  },
  {
    id: "deep-violet",
    label: "Violet Dream",
    gradient: "linear-gradient(135deg, #4c1d95 0%, #2563eb 100%)",
    from: "#4c1d95",
    to: "#2563eb",
  },
];

const STOCK_WALLPAPERS = [
  {
    id: "office",
    label: "Modern Office",
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "architecture",
    label: "City Skyscraper",
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "nature",
    label: "Atmospheric Mountains",
    url: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: "abstract",
    label: "Dark Minimalist Wave",
    url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1600&q=80",
  },
];

const THEME_COLOR_ROLES = [
  { id: "default", label: "Default", color: "transparent" },
  { id: "primary", label: "Primary", color: "var(--zirro-color-primary, #4f46e5)" },
  { id: "secondary", label: "Secondary", color: "var(--zirro-color-secondary, #0ea5e9)" },
  { id: "accent", label: "Accent", color: "var(--zirro-color-accent, #f59e0b)" },
  { id: "background", label: "Background", color: "var(--zirro-color-background, #ffffff)" },
  { id: "surface", label: "Surface", color: "var(--zirro-color-surface, #f8fafc)" },
  { id: "muted", label: "Muted", color: "var(--zirro-color-muted, #64748b)" },
  { id: "border", label: "Border", color: "var(--zirro-color-border, #e2e8f0)" },
];

const FONT_OPTIONS = [
  { id: "inter", label: "Inter (Modern Sans)" },
  { id: "roboto", label: "Roboto (Neutral Sans)" },
  { id: "open-sans", label: "Open Sans (Friendly Sans)" },
  { id: "lato", label: "Lato (Professional Sans)" },
  { id: "montserrat", label: "Montserrat (Geometric Display)" },
  { id: "dm-sans", label: "DM Sans (Minimalist)" },
  { id: "space-grotesk", label: "Space Grotesk (Tech Sans)" },
  { id: "ibm-plex-sans", label: "IBM Plex Sans (Corporate)" },
  { id: "playfair-display", label: "Playfair Display (Editorial Serif)" },
  { id: "merriweather", label: "Merriweather (Literary Serif)" },
  { id: "source-serif-4", label: "Source Serif 4 (Refined Serif)" },
  { id: "libre-baskerville", label: "Libre Baskerville (Classic Serif)" },
];

const OVERLAY_DIRECTIONS = [
  { id: "none", label: "Uniform (Full Tint)" },
  { id: "to-right", label: "Darken Left (Text on Left)" },
  { id: "to-left", label: "Darken Right (Text on Right)" },
  { id: "to-bottom", label: "Darken Top (Header Contrast)" },
  { id: "to-top", label: "Darken Bottom" },
  { id: "to-bottom-right", label: "Diagonal (Corner Gradient)" },
  { id: "radial", label: "Vignette (Radial Edge Tint)" },
];

export function SectionCustomizer({ node }: SectionCustomizerProps) {
  const viewport = useEditorStore((s) => s.viewport);
  const updateNodeProps = useEditorStore((s) => s.updateNodeProps);
  const selectNode = useEditorStore((s) => s.selectNode);
  const openMediaModal = useEditorStore((s) => s.openMediaModal);

  const [activeTab, setActiveTab] = useState<SectionPanelTab>("background");
  const [individualPadding, setIndividualPadding] = useState(false);
  const [individualMargin, setIndividualMargin] = useState(false);

  const currentNode = useEditorStore((s) => {
    for (const page of s.document.pages) {
      const match = page.nodes.find((n) => n.id === node.id);
      if (match) return match;
    }
    return node;
  }) ?? node;

  const props = currentNode.props ?? {};
  const overrides = (currentNode as { responsive?: Record<string, Record<string, unknown>> })
    .responsive?.[viewport] ?? {};
  const activeProps = viewport === "desktop" ? props : { ...props, ...overrides };

  const handlePropChange = (key: string, value: unknown) => {
    updateNodeProps(node.id, { [key]: value }, viewport);
  };

  const handleMultiplePropsChange = (patch: Record<string, unknown>) => {
    updateNodeProps(node.id, patch, viewport);
  };

  const isOverrideSet = (key: string) => {
    return viewport !== "desktop" && key in overrides;
  };

  // Background active type detection
  const hasBgImage = Boolean(activeProps.backgroundImage);
  const hasGradient = Boolean(
    activeProps.backgroundGradient ||
      (activeProps.gradientType && activeProps.gradientType !== "none"),
  );
  const currentBgColor = String(activeProps.backgroundColor ?? activeProps.background ?? "");

  // Real-time WCAG Contrast calculation
  const effectiveBg = hasBgImage
    ? activeProps.overlayEnabled
      ? blendOverlay(
          { r: 40, g: 45, b: 55 },
          parseColor(String(activeProps.overlayColor ?? "#000000")) ?? { r: 0, g: 0, b: 0 },
          Number(activeProps.overlayOpacity ?? 50) / 100,
        )
      : { r: 120, g: 125, b: 135 }
    : hasGradient
    ? parseColor(String(activeProps.gradientFrom ?? "#4f46e5")) ?? { r: 79, g: 70, b: 229 }
    : parseColor(currentBgColor) ?? { r: 255, g: 255, b: 255 };

  const activeTextColor = String(
    activeProps.textColor || activeProps.headingColor || (hasBgImage ? "#ffffff" : "#0f172a"),
  );
  const contrastEval = evaluateContrast(activeTextColor, effectiveBg);

  return (
    <div className="zirro-section-customizer" data-testid="section-customizer">
      {/* Header with Clean Hierarchy and Save Status */}
      <div className="zirro-section-customizer__header">
        <div className="zirro-section-customizer__title-row">
          <div className="zirro-section-customizer__badge">
            <span>Section Settings</span>
          </div>
          <span className="zirro-node-id">#{node.id}</span>
        </div>
        <p className="zirro-section-customizer__subtitle">
          Customize appearance, backgrounds, overlays, typography, and spacing for this section.
        </p>

        {viewport !== "desktop" ? (
          <div className="zirro-override-notice">
            <Zap size={12} style={{ display: "inline", marginRight: 4 }} />
            Editing overrides for <strong>{viewport}</strong> breakpoint.
          </div>
        ) : null}
      </div>

      {/* Navigation Pills */}
      <div className="zirro-section-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "background"}
          className={`zirro-section-tab-btn ${activeTab === "background" ? "active" : ""}`}
          onClick={() => setActiveTab("background")}
        >
          <Palette size={13} />
          Colors
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "image"}
          className={`zirro-section-tab-btn ${activeTab === "image" ? "active" : ""}`}
          onClick={() => setActiveTab("image")}
        >
          <ImageIcon size={13} />
          Image & Overlay
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "typography"}
          className={`zirro-section-tab-btn ${activeTab === "typography" ? "active" : ""}`}
          onClick={() => setActiveTab("typography")}
        >
          <Type size={13} />
          Typography
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "layout"}
          className={`zirro-section-tab-btn ${activeTab === "layout" ? "active" : ""}`}
          onClick={() => setActiveTab("layout")}
        >
          <Layout size={13} />
          Width & Layout
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "spacing"}
          className={`zirro-section-tab-btn ${activeTab === "spacing" ? "active" : ""}`}
          onClick={() => setActiveTab("spacing")}
        >
          <Sliders size={13} />
          Spacing
        </button>
      </div>

      {/* ── TAB 1: BACKGROUND & COLORS ────────────────────────────────────── */}
      {activeTab === "background" && (
        <div className="zirro-customizer-panel">
          {/* Quick Background Type Picker */}
          <div className="zirro-field">
            <span className="zirro-field-label">Background Style</span>
            <div className="zirro-segmented-control">
              <button
                type="button"
                className={`zirro-segment ${!hasGradient && !hasBgImage ? "active" : ""}`}
                onClick={() =>
                  handleMultiplePropsChange({
                    backgroundGradient: undefined,
                    gradientType: "none",
                    backgroundImage: null,
                    overlayEnabled: false,
                  })
                }
              >
                Solid Color
              </button>
              <button
                type="button"
                className={`zirro-segment ${hasGradient ? "active" : ""}`}
                onClick={() => {
                  if (!hasGradient) {
                    const preset = GRADIENT_PRESETS[0];
                    handleMultiplePropsChange({
                      gradientType: "linear",
                      gradientAngle: 135,
                      gradientFrom: preset.from,
                      gradientTo: preset.to,
                      backgroundGradient: preset.gradient,
                      backgroundImage: null,
                    });
                  }
                }}
              >
                Gradient
              </button>
              <button
                type="button"
                className={`zirro-segment ${hasBgImage ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("image");
                  if (!hasBgImage) {
                    handlePropChange("backgroundImage", STOCK_WALLPAPERS[0].url);
                  }
                }}
              >
                Image
              </button>
            </div>
          </div>

          {!hasGradient ? (
            <>
              {/* Theme Color Palette Chips */}
              <div className="zirro-field">
                <span className="zirro-field-label">
                  Theme Color Token {isOverrideSet("background") ? <Zap size={10} /> : ""}
                </span>
                <div className="zirro-color-chips-grid">
                  {THEME_COLOR_ROLES.map((role) => {
                    const isSelected =
                      role.id === "default"
                        ? !currentBgColor || currentBgColor === "transparent" || currentBgColor === "none"
                        : currentBgColor === role.id;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        className={`zirro-color-chip ${isSelected ? "zirro-color-chip--selected" : ""}`}
                        onClick={() =>
                          handlePropChange(
                            "background",
                            role.id === "default" ? "transparent" : role.id,
                          )
                        }
                        title={role.label}
                      >
                        <span
                          className="zirro-color-chip__swatch"
                          style={{
                            background:
                              role.id === "default"
                                ? "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)"
                                : role.color,
                            backgroundSize: role.id === "default" ? "6px 6px" : undefined,
                          }}
                        />
                        <span className="zirro-color-chip__name">{role.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Hex Color Picker */}
              <div className="zirro-field">
                <span className="zirro-field-label">Custom Background Color</span>
                <div className="zirro-color-input-row">
                  <input
                    type="color"
                    className="zirro-color-picker-input"
                    value={
                      currentBgColor.startsWith("#")
                        ? currentBgColor
                        : "#ffffff"
                    }
                    onChange={(e) => handlePropChange("background", e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="#ffffff or rgba(...)"
                    value={currentBgColor}
                    onChange={(e) => handlePropChange("background", e.target.value)}
                    className="zirro-hex-text-input"
                  />
                  {currentBgColor && currentBgColor !== "transparent" ? (
                    <button
                      type="button"
                      className="zirro-btn-mini"
                      onClick={() => handlePropChange("background", "transparent")}
                      title="Clear Color"
                    >
                      <RotateCcwIcon size={12} />
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            /* Gradient Controls */
            <div className="zirro-gradient-section">
              <div className="zirro-field">
                <span className="zirro-field-label">Gradient Presets</span>
                <div className="zirro-gradient-grid">
                  {GRADIENT_PRESETS.map((p) => {
                    const isSelected = activeProps.backgroundGradient === p.gradient;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`zirro-gradient-card ${isSelected ? "selected" : ""}`}
                        style={{ background: p.gradient }}
                        onClick={() =>
                          handleMultiplePropsChange({
                            gradientType: "linear",
                            gradientAngle: 135,
                            gradientFrom: p.from,
                            gradientTo: p.to,
                            backgroundGradient: p.gradient,
                          })
                        }
                        title={p.label}
                      >
                        {isSelected ? <Check size={14} color="#ffffff" /> : null}
                        <span className="zirro-gradient-card__label">{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Gradient Direction and Colors */}
              <div className="zirro-field-row-2">
                <label className="zirro-field">
                  <span className="zirro-field-label">Gradient Type</span>
                  <select
                    value={String(activeProps.gradientType ?? "linear")}
                    onChange={(e) => {
                      const type = e.target.value;
                      const from = String(activeProps.gradientFrom ?? "#4f46e5");
                      const to = String(activeProps.gradientTo ?? "#ec4899");
                      const angle = Number(activeProps.gradientAngle ?? 135);
                      const grad =
                        type === "linear"
                          ? `linear-gradient(${angle}deg, ${from}, ${to})`
                          : `radial-gradient(circle, ${from}, ${to})`;
                      handleMultiplePropsChange({
                        gradientType: type,
                        backgroundGradient: grad,
                      });
                    }}
                  >
                    <option value="linear">Linear</option>
                    <option value="radial">Radial</option>
                  </select>
                </label>

                {activeProps.gradientType !== "radial" ? (
                  <label className="zirro-field">
                    <span className="zirro-field-label">
                      Angle ({Number(activeProps.gradientAngle ?? 135)}°)
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={360}
                      step={15}
                      value={Number(activeProps.gradientAngle ?? 135)}
                      onChange={(e) => {
                        const angle = Number(e.target.value);
                        const from = String(activeProps.gradientFrom ?? "#4f46e5");
                        const to = String(activeProps.gradientTo ?? "#ec4899");
                        handleMultiplePropsChange({
                          gradientAngle: angle,
                          backgroundGradient: `linear-gradient(${angle}deg, ${from}, ${to})`,
                        });
                      }}
                    />
                  </label>
                ) : null}
              </div>

              <div className="zirro-field-row-2">
                <div className="zirro-field">
                  <span className="zirro-field-label">Start Color</span>
                  <div className="zirro-color-input-row">
                    <input
                      type="color"
                      className="zirro-color-picker-input"
                      value={String(activeProps.gradientFrom ?? "#4f46e5")}
                      onChange={(e) => {
                        const from = e.target.value;
                        const to = String(activeProps.gradientTo ?? "#ec4899");
                        const angle = Number(activeProps.gradientAngle ?? 135);
                        handleMultiplePropsChange({
                          gradientFrom: from,
                          backgroundGradient: `linear-gradient(${angle}deg, ${from}, ${to})`,
                        });
                      }}
                    />
                    <input
                      type="text"
                      value={String(activeProps.gradientFrom ?? "#4f46e5")}
                      onChange={(e) => {
                        const from = e.target.value;
                        const to = String(activeProps.gradientTo ?? "#ec4899");
                        const angle = Number(activeProps.gradientAngle ?? 135);
                        handleMultiplePropsChange({
                          gradientFrom: from,
                          backgroundGradient: `linear-gradient(${angle}deg, ${from}, ${to})`,
                        });
                      }}
                      className="zirro-hex-text-input"
                    />
                  </div>
                </div>

                <div className="zirro-field">
                  <span className="zirro-field-label">End Color</span>
                  <div className="zirro-color-input-row">
                    <input
                      type="color"
                      className="zirro-color-picker-input"
                      value={String(activeProps.gradientTo ?? "#ec4899")}
                      onChange={(e) => {
                        const to = e.target.value;
                        const from = String(activeProps.gradientFrom ?? "#4f46e5");
                        const angle = Number(activeProps.gradientAngle ?? 135);
                        handleMultiplePropsChange({
                          gradientTo: to,
                          backgroundGradient: `linear-gradient(${angle}deg, ${from}, ${to})`,
                        });
                      }}
                    />
                    <input
                      type="text"
                      value={String(activeProps.gradientTo ?? "#ec4899")}
                      onChange={(e) => {
                        const to = e.target.value;
                        const from = String(activeProps.gradientFrom ?? "#4f46e5");
                        const angle = Number(activeProps.gradientAngle ?? 135);
                        handleMultiplePropsChange({
                          gradientTo: to,
                          backgroundGradient: `linear-gradient(${angle}deg, ${from}, ${to})`,
                        });
                      }}
                      className="zirro-hex-text-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: BACKGROUND IMAGE, OVERLAYS & CONTRAST ─────────────────── */}
      {activeTab === "image" && (
        <div className="zirro-customizer-panel">
          {/* Image Source */}
          <label className="zirro-field">
            <span className="zirro-field-label">Background Image URL</span>
            <div className="zirro-input-with-btn">
              <input
                type="text"
                placeholder="https://... or uploaded image"
                value={String(activeProps.backgroundImage ?? "")}
                onChange={(e) => handlePropChange("backgroundImage", e.target.value || null)}
              />
              <button
                type="button"
                className="zirro-btn zirro-btn-secondary"
                onClick={() =>
                  openMediaModal((url) => {
                    handleMultiplePropsChange({
                      backgroundImage: url,
                      overlayEnabled: true,
                      overlayOpacity: activeProps.overlayOpacity ?? 50,
                      overlayColor: activeProps.overlayColor ?? "#000000",
                      textColor: activeProps.textColor ?? "#ffffff",
                    });
                  })
                }
              >
                <Upload size={13} />
                Browse
              </button>
            </div>
          </label>

          {/* Quick Sample Wallpapers */}
          <div className="zirro-field">
            <span className="zirro-field-label">High-Resolution Wallpapers</span>
            <div className="zirro-wallpaper-grid">
              {STOCK_WALLPAPERS.map((w) => {
                const isSelected = activeProps.backgroundImage === w.url;
                return (
                  <button
                    key={w.id}
                    type="button"
                    className={`zirro-wallpaper-btn ${isSelected ? "selected" : ""}`}
                    onClick={() =>
                      handleMultiplePropsChange({
                        backgroundImage: w.url,
                        overlayEnabled: true,
                        overlayOpacity: activeProps.overlayOpacity ?? 50,
                        overlayColor: activeProps.overlayColor ?? "#000000",
                        textColor: activeProps.textColor ?? "#ffffff",
                      })
                    }
                  >
                    <img src={w.url} alt={w.label} />
                    <span className="zirro-wallpaper-label">{w.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Directional Overlay Presets */}
          <div className="zirro-field">
            <span className="zirro-field-label">Quick Directional Overlays</span>
            <div className="zirro-quick-tint-buttons" style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {OVERLAY_DIRECTIONS.filter((d) => d.id !== "none").map((dir) => {
                const label = dir.id.replace(/-/g, " ");
                const isSelected = activeProps.overlayDirection === dir.id && activeProps.overlayEnabled;
                return (
                  <button
                    key={dir.id}
                    type="button"
                    className={`zirro-btn-compact ${isSelected ? "active" : ""}`}
                    onClick={() =>
                      handleMultiplePropsChange({
                        overlayEnabled: true,
                        overlayDirection: dir.id,
                        overlayOpacity: activeProps.overlayOpacity ?? 50,
                      })
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeProps.backgroundImage ? (
            <>
              {/* Preview & Remove */}
              <div className="zirro-image-preview-card">
                <img
                  src={String(activeProps.backgroundImage)}
                  alt="Section background"
                  className="zirro-image-preview-thumb"
                />
                <div className="zirro-image-preview-info">
                  <span className="zirro-image-preview-status">Active Background</span>
                  <button
                    type="button"
                    className="zirro-btn-text zirro-btn-danger-text"
                    onClick={() =>
                      handleMultiplePropsChange({
                        backgroundImage: null,
                        overlayEnabled: false,
                      })
                    }
                  >
                    <Trash2 size={12} /> Remove Image
                  </button>
                </div>
              </div>

              {/* Image Sizing and Positioning */}
              <div className="zirro-field-row-2">
                <label className="zirro-field">
                  <span className="zirro-field-label">Image Sizing</span>
                  <select
                    value={String(activeProps.backgroundSize ?? "cover")}
                    onChange={(e) => handlePropChange("backgroundSize", e.target.value)}
                  >
                    <option value="cover">Cover (Fill entire section)</option>
                    <option value="contain">Contain (Fit inside)</option>
                    <option value="auto">Auto (Natural size)</option>
                  </select>
                </label>

                <label className="zirro-field">
                  <span className="zirro-field-label">Position / Focal Point</span>
                  <select
                    value={String(activeProps.backgroundPosition ?? "center")}
                    onChange={(e) => handlePropChange("backgroundPosition", e.target.value)}
                  >
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </label>
              </div>

              <div className="zirro-field-row-2">
                <label className="zirro-field">
                  <span className="zirro-field-label">Attachment Effect</span>
                  <select
                    value={String(activeProps.backgroundAttachment ?? "scroll")}
                    onChange={(e) => handlePropChange("backgroundAttachment", e.target.value)}
                  >
                    <option value="scroll">Scroll Normal</option>
                    <option value="fixed">Fixed (Parallax Effect)</option>
                  </select>
                </label>

                <label className="zirro-field">
                  <span className="zirro-field-label">Repeat</span>
                  <select
                    value={String(activeProps.backgroundRepeat ?? "no-repeat")}
                    onChange={(e) => handlePropChange("backgroundRepeat", e.target.value)}
                  >
                    <option value="no-repeat">No Repeat</option>
                    <option value="repeat">Repeat</option>
                  </select>
                </label>
              </div>

              {/* ── OVERLAY CONTROLS ── */}
              <div className="zirro-field-group">
                <div className="zirro-switch-header">
                  <div>
                    <h5>Readability Color Overlay</h5>
                    <p className="zirro-hint" style={{ margin: 0 }}>
                      Placing an overlay ensures crisp text contrast over photos.
                    </p>
                  </div>
                  <label className="zirro-toggle-switch">
                    <input
                      type="checkbox"
                      checked={Boolean(activeProps.overlayEnabled)}
                      onChange={(e) =>
                        handleMultiplePropsChange({
                          overlayEnabled: e.target.checked,
                          overlayOpacity: e.target.checked ? (activeProps.overlayOpacity ?? 50) : 0,
                        })
                      }
                    />
                    <span className="zirro-toggle-slider" />
                  </label>
                </div>

                {activeProps.overlayEnabled ? (
                  <div className="zirro-overlay-controls">
                    {/* Opacity Slider */}
                    <label className="zirro-field">
                      <div className="zirro-field-label-row">
                        <span>Overlay Opacity</span>
                        <span className="zirro-badge">
                          {Number(activeProps.overlayOpacity ?? 50)}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={Number(activeProps.overlayOpacity ?? 50)}
                        onChange={(e) =>
                          handlePropChange("overlayOpacity", Number(e.target.value))
                        }
                      />
                    </label>

                    {/* Overlay Direction / Gradient Overlays */}
                    <label className="zirro-field">
                      <span className="zirro-field-label">Overlay Direction & Gradients</span>
                      <select
                        value={String(activeProps.overlayDirection ?? "none")}
                        onChange={(e) => handlePropChange("overlayDirection", e.target.value)}
                      >
                        {OVERLAY_DIRECTIONS.map((dir) => (
                          <option key={dir.id} value={dir.id}>
                            {dir.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {/* Overlay Tint Color */}
                    <div className="zirro-field">
                      <span className="zirro-field-label">Overlay Tint Color</span>
                      <div className="zirro-color-input-row">
                        <input
                          type="color"
                          className="zirro-color-picker-input"
                          value={
                            String(activeProps.overlayColor ?? "#000000").startsWith("#")
                              ? String(activeProps.overlayColor)
                              : "#000000"
                          }
                          onChange={(e) => handlePropChange("overlayColor", e.target.value)}
                        />
                        <div className="zirro-quick-tint-buttons">
                          <button
                            type="button"
                            className="zirro-ed-btn"
                            onClick={() => handlePropChange("overlayColor", "#000000")}
                          >
                            Dark (#000)
                          </button>
                          <button
                            type="button"
                            className="zirro-ed-btn"
                            onClick={() => handlePropChange("overlayColor", "#0f172a")}
                          >
                            Slate (#0f172a)
                          </button>
                          <button
                            type="button"
                            className="zirro-ed-btn"
                            onClick={() => handlePropChange("overlayColor", "#ffffff")}
                          >
                            Light (#fff)
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* ── WCAG CONTRAST GUIDANCE CARD ── */}
              <div className={`zirro-contrast-card zirro-contrast-card--${contrastEval.rating}`}>
                <div className="zirro-contrast-card__header">
                  {contrastEval.rating === "poor" ? (
                    <ShieldAlert size={16} className="zirro-contrast-icon--warning" />
                  ) : (
                    <ShieldCheck size={16} className="zirro-contrast-icon--success" />
                  )}
                  <div className="zirro-contrast-card__title-box">
                    <strong>Contrast Ratio: {contrastEval.scoreText}</strong>
                    <span className="zirro-contrast-badge">{contrastEval.rating.toUpperCase()}</span>
                  </div>
                </div>

                <p className="zirro-contrast-card__desc">{contrastEval.message}</p>

                {contrastEval.rating === "poor" ? (
                  <div className="zirro-contrast-actions">
                    <p className="zirro-hint" style={{ color: "var(--ed-text)", marginBottom: 8 }}>
                      Recommended Legibility Fixes:
                    </p>
                    <div className="zirro-contrast-btn-group">
                      <button
                        type="button"
                        className="zirro-btn-compact"
                        onClick={() =>
                          handleMultiplePropsChange({
                            overlayEnabled: true,
                            overlayOpacity: 55,
                            overlayColor: "#000000",
                            textColor: "#ffffff",
                          })
                        }
                      >
                        Apply Dark 55% Overlay + Light Text
                      </button>
                      <button
                        type="button"
                        className="zirro-btn-compact"
                        onClick={() =>
                          handleMultiplePropsChange({
                            overlayEnabled: true,
                            overlayDirection: "to-right",
                            overlayOpacity: 75,
                            overlayColor: "#000000",
                            textColor: "#ffffff",
                          })
                        }
                      >
                        Apply Left Dark Gradient (for hero titles)
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <p className="zirro-hint">
              Select or browse a background image to enable image sizing, positioning, and contrast overlays.
            </p>
          )}
        </div>
      )}

      {/* ── TAB 3: TYPOGRAPHY & TEXT STYLING ─────────────────────────────── */}
      {activeTab === "typography" && (
        <div className="zirro-customizer-panel">
          <p className="zirro-hint">
            Configure section-wide text colors and typography, or pick a specific child element to style independently.
          </p>

          {/* Section Font Family */}
          <label className="zirro-field">
            <span className="zirro-field-label">Section Font Family</span>
            <select
              value={String(activeProps.fontFamily ?? "inherit")}
              onChange={(e) =>
                handlePropChange("fontFamily", e.target.value === "inherit" ? undefined : e.target.value)
              }
            >
              <option value="inherit">Inherit Theme Font</option>
              {FONT_OPTIONS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>

          {/* Section-Level Text Color Controls */}
          <div className="zirro-field-group">
            <h5>Section Text Colors</h5>

            {/* General Text Color */}
            <div className="zirro-field">
              <span className="zirro-field-label">General Text Color</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(activeProps.textColor ?? "#ffffff").startsWith("#")
                      ? String(activeProps.textColor)
                      : "#ffffff"
                  }
                  onChange={(e) => handlePropChange("textColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="e.g. #ffffff or #0f172a"
                  value={String(activeProps.textColor ?? "")}
                  onChange={(e) => handlePropChange("textColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                <div className="zirro-quick-tint-buttons">
                  <button
                    type="button"
                    className="zirro-ed-btn"
                    onClick={() => handlePropChange("textColor", "#ffffff")}
                  >
                    White
                  </button>
                  <button
                    type="button"
                    className="zirro-ed-btn"
                    onClick={() => handlePropChange("textColor", "#0f172a")}
                  >
                    Dark
                  </button>
                  <button
                    type="button"
                    className="zirro-ed-btn"
                    onClick={() => handlePropChange("textColor", "var(--zirro-color-primary)")}
                  >
                    Primary
                  </button>
                </div>
              </div>
            </div>

            {/* Heading Color Specific */}
            <div className="zirro-field">
              <span className="zirro-field-label">Headings Color (Optional Override)</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(activeProps.headingColor ?? "#ffffff").startsWith("#")
                      ? String(activeProps.headingColor)
                      : "#ffffff"
                  }
                  onChange={(e) => handlePropChange("headingColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Inherit from section text color"
                  value={String(activeProps.headingColor ?? "")}
                  onChange={(e) => handlePropChange("headingColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {activeProps.headingColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("headingColor", undefined)}
                    title="Reset to general text color"
                  >
                    <RotateCcwIcon size={12} />
                  </button>
                ) : null}
              </div>
            </div>

            {/* Paragraph / Body Color Specific */}
            <div className="zirro-field">
              <span className="zirro-field-label">Paragraph Body Color (Optional)</span>
              <div className="zirro-color-input-row">
                <input
                  type="color"
                  className="zirro-color-picker-input"
                  value={
                    String(activeProps.bodyColor ?? "#e2e8f0").startsWith("#")
                      ? String(activeProps.bodyColor)
                      : "#e2e8f0"
                  }
                  onChange={(e) => handlePropChange("bodyColor", e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Inherit from section text color"
                  value={String(activeProps.bodyColor ?? "")}
                  onChange={(e) => handlePropChange("bodyColor", e.target.value || undefined)}
                  className="zirro-hex-text-input"
                />
                {activeProps.bodyColor ? (
                  <button
                    type="button"
                    className="zirro-btn-mini"
                    onClick={() => handlePropChange("bodyColor", undefined)}
                    title="Reset to general text color"
                  >
                    <RotateCcwIcon size={12} />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Child Elements Quick Selection */}
          {node.children && node.children.length > 0 ? (
            <div className="zirro-field-group">
              <h5>Elements in this Section</h5>
              <p className="zirro-hint" style={{ margin: "0 0 8px 0" }}>
                Click any element below to customize its individual typography, sizes, and spacing:
              </p>
              <div className="zirro-element-picker-list">
                {node.children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    className="zirro-element-picker-item"
                    onClick={() => selectNode(child.id)}
                  >
                    <span className="zirro-element-picker-type">{child.type}</span>
                    <span className="zirro-element-picker-preview">
                      {child.type === "Heading"
                        ? String(child.props.text ?? "Heading")
                        : child.type === "Text"
                        ? Array.isArray(child.props.content) && child.props.content[0]
                          ? String((child.props.content[0] as { text: string }).text).slice(0, 30) + "..."
                          : "Paragraph"
                        : child.type === "Button"
                        ? String(child.props.label ?? "Button")
                        : child.type}
                    </span>
                    <ArrowRight size={12} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* ── TAB 4: WIDTH & LAYOUT ────────────────────────────────────────── */}
      {activeTab === "layout" && (
        <div className="zirro-customizer-panel">
          {/* Full-Width vs Contained */}
          <div className="zirro-field">
            <span className="zirro-field-label">Section Viewport Width</span>
            <div className="zirro-segmented-control">
              <button
                type="button"
                className={`zirro-segment ${activeProps.width !== "contained" ? "active" : ""}`}
                onClick={() => handlePropChange("width", "full")}
              >
                <Maximize2 size={13} />
                Full Width
              </button>
              <button
                type="button"
                className={`zirro-segment ${activeProps.width === "contained" ? "active" : ""}`}
                onClick={() => handlePropChange("width", "contained")}
              >
                <Minimize2 size={13} />
                Boxed / Contained
              </button>
            </div>
            <p className="zirro-hint" style={{ marginTop: 4 }}>
              {activeProps.width === "contained"
                ? "Section is centered with outer margins on larger screens."
                : "Section expands to the full edge of the browser viewport."}
            </p>
          </div>

          {/* Content Max Width */}
          <label className="zirro-field">
            <span className="zirro-field-label">Inner Content Max-Width</span>
            <select
              value={String(activeProps.contentWidth ?? (activeProps.width === "full" ? "wide" : "normal"))}
              onChange={(e) => handlePropChange("contentWidth", e.target.value)}
            >
              <option value="narrow">Narrow (640px — Focused readability)</option>
              <option value="normal">Standard (768px — Compact layout)</option>
              <option value="wide">Wide (1140px — Modern responsive web)</option>
              <option value="full">Full Bleed (100% — Edge-to-edge content)</option>
            </select>
          </label>

          {/* Section Minimum Height */}
          <label className="zirro-field">
            <span className="zirro-field-label">Section Height</span>
            <select
              value={String(activeProps.minHeight ?? "auto")}
              onChange={(e) => handlePropChange("minHeight", e.target.value)}
            >
              <option value="auto">Auto (Fits content size)</option>
              <option value="sm">Compact (240px)</option>
              <option value="md">Medium (400px)</option>
              <option value="lg">Hero Section (600px)</option>
              <option value="screen">Full Screen (100vh Viewport Height)</option>
            </select>
          </label>

          {/* Direction & Columns */}
          <div className="zirro-field-group">
            <h5>Child Elements Layout</h5>

            <div className="zirro-field">
              <span className="zirro-field-label">Flow Direction</span>
              <div className="zirro-segmented-control">
                <button
                  type="button"
                  className={`zirro-segment ${activeProps.direction !== "horizontal" ? "active" : ""}`}
                  onClick={() => handlePropChange("direction", "vertical")}
                >
                  Vertical (Stacked)
                </button>
                <button
                  type="button"
                  className={`zirro-segment ${activeProps.direction === "horizontal" ? "active" : ""}`}
                  onClick={() => handlePropChange("direction", "horizontal")}
                >
                  Horizontal (Row)
                </button>
              </div>
            </div>

            <label className="zirro-field">
              <span className="zirro-field-label">
                <ColumnsIcon size={12} style={{ display: "inline", marginRight: 4 }} />
                Grid Columns
              </span>
              <select
                value={Number(activeProps.columns ?? 1)}
                onChange={(e) => handlePropChange("columns", Number(e.target.value))}
              >
                <option value={1}>1 Column (Standard)</option>
                <option value={2}>2 Columns (Split 50 / 50)</option>
                <option value={3}>3 Columns (Features / Showcase)</option>
                <option value={4}>4 Columns (Stats / Gallery)</option>
              </select>
            </label>

            {/* Content Alignment */}
            <div className="zirro-field">
              <span className="zirro-field-label">Alignment</span>
              <div className="zirro-segmented-control">
                {[
                  { value: "start", label: "Start", icon: AlignLeft },
                  { value: "center", label: "Center", icon: AlignCenter },
                  { value: "end", label: "End", icon: AlignRight },
                  { value: "stretch", label: "Stretch", icon: ColumnsIcon },
                ].map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`zirro-segment ${activeProps.align === opt.value ? "active" : ""}`}
                      onClick={() => handlePropChange("align", opt.value)}
                      title={opt.label}
                    >
                      <Icon size={13} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 5: SPACING & BORDERS ───────────────────────────────────────── */}
      {activeTab === "spacing" && (
        <div className="zirro-customizer-panel">
          {/* Padding Controls */}
          <div className="zirro-spacing-header">
            <span className="zirro-field-label">Section Padding</span>
            <button
              type="button"
              className="zirro-btn-text"
              onClick={() => setIndividualPadding(!individualPadding)}
            >
              {individualPadding ? "Unified Padding" : "Individual Sides"}
            </button>
          </div>

          {!individualPadding ? (
            <label className="zirro-field">
              <span className="zirro-field-label">
                Padding Size {isOverrideSet("padding") ? <Zap size={10} /> : ""}
              </span>
              <select
                value={String(activeProps.padding ?? "LG")}
                onChange={(e) => {
                  handleMultiplePropsChange({
                    padding: e.target.value,
                    paddingTop: undefined,
                    paddingBottom: undefined,
                    paddingLeft: undefined,
                    paddingRight: undefined,
                  });
                }}
              >
                <option value="none">None (0px)</option>
                <option value="XS">XS (4px)</option>
                <option value="SM">SM (8px)</option>
                <option value="MD">MD (16px)</option>
                <option value="LG">LG (32px — Standard)</option>
                <option value="XL">XL (64px — Spacious Hero)</option>
              </select>
            </label>
          ) : (
            <div className="zirro-quad-inputs-grid">
              <label className="zirro-field">
                <span>Top (px)</span>
                <input
                  type="text"
                  placeholder="32px"
                  value={String(activeProps.paddingTop ?? "")}
                  onChange={(e) => handlePropChange("paddingTop", e.target.value)}
                />
              </label>
              <label className="zirro-field">
                <span>Bottom (px)</span>
                <input
                  type="text"
                  placeholder="32px"
                  value={String(activeProps.paddingBottom ?? "")}
                  onChange={(e) => handlePropChange("paddingBottom", e.target.value)}
                />
              </label>
              <label className="zirro-field">
                <span>Left (px)</span>
                <input
                  type="text"
                  placeholder="24px"
                  value={String(activeProps.paddingLeft ?? "")}
                  onChange={(e) => handlePropChange("paddingLeft", e.target.value)}
                />
              </label>
              <label className="zirro-field">
                <span>Right (px)</span>
                <input
                  type="text"
                  placeholder="24px"
                  value={String(activeProps.paddingRight ?? "")}
                  onChange={(e) => handlePropChange("paddingRight", e.target.value)}
                />
              </label>
            </div>
          )}

          {/* Margins */}
          <div className="zirro-spacing-header" style={{ marginTop: 16 }}>
            <span className="zirro-field-label">Section Outer Margins</span>
            <button
              type="button"
              className="zirro-btn-text"
              onClick={() => setIndividualMargin(!individualMargin)}
            >
              {individualMargin ? "Quick Margins" : "Custom Sides"}
            </button>
          </div>

          {!individualMargin ? (
            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span className="zirro-field-label">Margin Top</span>
                <select
                  value={String(activeProps.marginTop ?? "none")}
                  onChange={(e) => handlePropChange("marginTop", e.target.value === "none" ? undefined : e.target.value)}
                >
                  <option value="none">None (0px)</option>
                  <option value="SM">SM (8px)</option>
                  <option value="MD">MD (16px)</option>
                  <option value="LG">LG (32px)</option>
                  <option value="XL">XL (64px)</option>
                </select>
              </label>

              <label className="zirro-field">
                <span className="zirro-field-label">Margin Bottom</span>
                <select
                  value={String(activeProps.marginBottom ?? "none")}
                  onChange={(e) => handlePropChange("marginBottom", e.target.value === "none" ? undefined : e.target.value)}
                >
                  <option value="none">None (0px)</option>
                  <option value="SM">SM (8px)</option>
                  <option value="MD">MD (16px)</option>
                  <option value="LG">LG (32px)</option>
                  <option value="XL">XL (64px)</option>
                </select>
              </label>
            </div>
          ) : (
            <div className="zirro-quad-inputs-grid">
              <label className="zirro-field">
                <span>Margin Top</span>
                <input
                  type="text"
                  placeholder="0px"
                  value={String(activeProps.marginTop ?? "")}
                  onChange={(e) => handlePropChange("marginTop", e.target.value)}
                />
              </label>
              <label className="zirro-field">
                <span>Margin Bottom</span>
                <input
                  type="text"
                  placeholder="0px"
                  value={String(activeProps.marginBottom ?? "")}
                  onChange={(e) => handlePropChange("marginBottom", e.target.value)}
                />
              </label>
            </div>
          )}

          {/* Child Gap */}
          <label className="zirro-field" style={{ marginTop: 12 }}>
            <span className="zirro-field-label">
              Gap between Child Elements {isOverrideSet("gap") ? <Zap size={10} /> : ""}
            </span>
            <select
              value={String(activeProps.gap ?? "MD")}
              onChange={(e) => handlePropChange("gap", e.target.value)}
            >
              <option value="none">None (0px)</option>
              <option value="XS">XS (4px)</option>
              <option value="SM">SM (8px)</option>
              <option value="MD">MD (16px — Standard)</option>
              <option value="LG">LG (32px — Spacious)</option>
              <option value="XL">XL (64px)</option>
            </select>
          </label>

          {/* Borders & Dividers */}
          <div className="zirro-field-group">
            <h5>Borders & Dividers</h5>

            <div className="zirro-field-row-2">
              <label className="zirro-field">
                <span className="zirro-field-label">Divider Position</span>
                <select
                  value={String(activeProps.borderPosition ?? "none")}
                  onChange={(e) => handlePropChange("borderPosition", e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="bottom">Bottom Divider</option>
                  <option value="top">Top Divider</option>
                  <option value="y">Top & Bottom Dividers</option>
                  <option value="all">Full Box Border</option>
                </select>
              </label>

              {activeProps.borderPosition && activeProps.borderPosition !== "none" ? (
                <label className="zirro-field">
                  <span className="zirro-field-label">Border Width</span>
                  <select
                    value={String(activeProps.borderWidth ?? "1px")}
                    onChange={(e) => handlePropChange("borderWidth", e.target.value)}
                  >
                    <option value="1px">1px (Subtle)</option>
                    <option value="2px">2px (Medium)</option>
                    <option value="4px">4px (Bold)</option>
                  </select>
                </label>
              ) : null}
            </div>

            <label className="zirro-field">
              <span className="zirro-field-label">Rounded Corners (Border Radius)</span>
              <select
                value={String(activeProps.borderRadius ?? "none")}
                onChange={(e) => handlePropChange("borderRadius", e.target.value)}
              >
                <option value="none">None (0px — Sharp edges)</option>
                <option value="sm">Small (4px)</option>
                <option value="md">Medium (8px)</option>
                <option value="lg">Large (16px)</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function RotateCcwIcon(props: { size?: number }) {
  return <RefreshCw size={props.size ?? 12} />;
}
